import React, { useState } from 'react';
import { ArrowLeft, ChevronRight, SkipForward } from 'lucide-react';

const QuestionInterfaceRefined = () => {
  const [selectedOption, setSelectedOption] = useState('option1');
  const [response, setResponse] = useState('');
    const [showAutoSave, setShowAutoSave] = useState(false);
    const wordCount = response.trim().split(/\s+/).filter(Boolean).length;
    const hasMinimumDetail = wordCount >= 50;

    React.useEffect(() => {
      if (response.length > 0) {
        setShowAutoSave(true);
        const timer = setTimeout(() => setShowAutoSave(false), 2000);
        return () => clearTimeout(timer);
      }
    }, [response]);

    return (
        <div className="min-h-screen bg-stone-100">
      <div className="min-h-screen bg-stone-50 p-8">
        <button className="flex items-center gap-2 text-slate-700 text-sm mb-8 hover:text-slate-900 transition-colors">
          <ArrowLeft size={16} />
          Back to Ideas Hub
        </button>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white border-2 border-slate-900 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="bg-slate-900 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                  <span className="text-sm font-bold text-white">Question 1 of 8</span>
                  <span className="text-stone-400 text-xs">• ~2 min remaining</span>
                </div>
                <div className="flex items-center gap-4">
                  {showAutoSave && (
                    <span className="text-xs text-teal-300 animate-fadeIn">Saved</span>
                  )}
                  <span className="text-sm text-stone-300">13% complete</span>
                </div>
              </div>
            </div>
            <div className="relative h-2 bg-stone-200">
              <div 
                className="absolute inset-y-0 left-0 bg-indigo-300 bg-[length:200%_100%] transition-all duration-500"
                style={{ width: '13%' }}
              ></div>
            </div>
          </div>

          <div className="bg-white border border-slate-900 rounded-xl shadow-sm">
            <div className="p-8 pb-6">
              <h1 className="text-base font-semibold text-slate-900 mb-4 leading-relaxed">
                Describe the last dinner conversation you had where someone said something that made you 
                think differently about something.
              </h1>

              {/* Prominent guidance callout */}
              <div className="mb-6 bg-violet-50 border-l-4 border-l-indigo-600 rounded-r-lg p-4">
                <p className="text-xs font-semibold text-indigo-900 mb-1">What makes a great response:</p>
                <p className="text-xs text-slate-900">
                  Include specific details – what you <span className="font-semibold text-indigo-900">saw</span>, <span className="font-semibold text-indigo-900">heard</span>, <span className="font-semibold text-indigo-900">felt</span>, or <span className="font-semibold text-indigo-900">thought</span>. The more you provide, the more I can help you spot the non-obvious angles that make this story memorable.
                </p>
              </div>

              <div className="relative">
                <textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="What were you eating? Who said it? What exact words do you remember?"
                  className="w-full min-h-[280px] p-4 border-2 border-slate-300 rounded-lg text-slate-900 text-sm placeholder:text-stone-500 focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 transition-all resize-none"
                />
                
                {wordCount > 0 && (
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    {hasMinimumDetail ? (
                      <div className="flex items-center gap-1.5 text-teal-600 text-xs font-medium bg-teal-50 px-2 py-1 rounded">
                        <div className="w-1 h-1 bg-teal-600 rounded-full"></div>
                        Good detail
                      </div>
                    ) : (
                      <div className="text-stone-500 text-xs">
                        {wordCount} words • Keep going...
                      </div>
                    )}
                  </div>
                )}
              </div>

              {wordCount === 0 && (
                <button className="mt-3 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                  Show me an example response →
                </button>
              )}
            </div>

            <div className="bg-stone-50 px-8 py-5 border-t border-stone-200 rounded-b-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm">
                  <p className="text-stone-600 text-xs flex items-center gap-2">
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    0 of 4 minimum questions answered
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    Press Ctrl+Enter to submit
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button className="px-5 py-2.5 text-slate-700 text-sm font-medium hover:text-slate-900 transition-colors flex items-center gap-1.5">
                    <SkipForward size={14} />
                    Skip
                  </button>
                  <button className="bg-indigo-600 text-white px-6 py-2.5 rounded-lg border-2 border-slate-900 font-medium text-sm hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2 shadow-sm">
                    Next Question
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              <div className="pt-3 border-t border-stone-200">
                <div className="flex items-start gap-2">
                  <div className="w-1 h-1 bg-indigo-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div className="text-xs text-stone-600">
                    <span className="font-semibold text-slate-700">COMING NEXT:</span> Tell us about a time you changed your mind about someone
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionInterfaceRefined;